import { useEffect, useCallback, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { SeverityBadge } from "@/components/security/SeverityBadge";
import { cn, humanCategory } from "@/lib/util";
import { ArrowLeft, ExternalLink, ShieldAlert, Save, Skull, Calendar, Wrench, Building2, Server } from "lucide-react";
import { toast } from "sonner";
import { FindingAIChat } from "@/components/security/FindingAIChat";
import { RequestTakedownButton } from "@/components/security/RequestTakedownButton";

const fmtDate = (v) => {
  if (!v) return "—";
  const d = new Date(v);
  return isNaN(d.getTime()) ? "—" : d.toLocaleDateString();
};

export default function FindingDetail() {
  const { id } = useParams();
  const [f, setF] = useState(null);
  const [status, setStatus] = useState("");
  const [owner, setOwner] = useState("");

  const load = useCallback(async () => {
    const r = await api.get(`/findings/${id}`);
    setF(r.data);
    setStatus(r.data.status);
    setOwner(r.data.owner || "");
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    try {
      await api.patch(`/findings/${id}`, { status, owner: owner || null });
      toast.success("Hallazgo actualizado");
      load();
    } catch (e) {
      toast.error("Falló la actualización");
    }
  };

  if (!f) return <div className="text-muted-foreground font-mono">Cargando...</div>;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="finding-detail">
      <Link to="/findings" className="inline-flex items-center gap-2 text-xs text-muted-foreground hover:text-foreground">
        <ArrowLeft className="w-3 h-3" /> Volver a hallazgos
      </Link>

      <header className="flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <SeverityBadge sev={f.severity} />
            {f.kev && <span className="font-mono text-[10px] tracking-widest uppercase border border-sev-critical text-sev-critical px-2 py-0.5 bg-sev-critical/10">CISA KEV</span>}
            <span className="font-mono text-[10px] tracking-widest uppercase text-muted-foreground">{humanCategory(f.category)}</span>
          </div>
          <h1 className="font-display text-3xl md:text-4xl font-medium tracking-tight">{f.title}</h1>
          {f.cve && <div className="font-mono text-sm text-primary mt-2">{f.cve}</div>}
          <div className="mt-3">
            <RequestTakedownButton finding={f} />
          </div>
        </div>
        <div className="text-right">
          <div className="overline">Puntaje de riesgo</div>
          <div className={cn("font-display font-bold leading-none mt-1",
            f.risk_score >= 80 ? "text-sev-critical" : f.risk_score >= 60 ? "text-sev-high" : f.risk_score >= 40 ? "text-sev-medium" : "text-sev-low"
          )} style={{ fontSize: "72px" }} data-testid="risk-score">{f.risk_score}</div>
          <div className="font-mono text-[10px] text-muted-foreground tracking-widest uppercase">/100 contextual</div>
        </div>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <section className="border border-border bg-card/60 p-5">
            <div className="overline mb-2">Descripción</div>
            <p className="text-sm leading-relaxed whitespace-pre-wrap">{f.description || "—"}</p>
          </section>

          {/* Exposure — always visible, describes raw scan facts */}
          <section className="border border-border bg-card/60 p-5" data-testid="exposure-section">
            <div className="flex items-center gap-2 mb-2">
              <Server className="w-4 h-4 text-primary" />
              <div className="overline">Exposición · datos del escaneo</div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs font-mono">
              <div className="border border-border/60 bg-background/40 p-2">
                <div className="overline text-[9px]">Tipo</div>
                <div className="mt-1">{f.exposure?.kind || "—"}</div>
              </div>
              <div className="border border-border/60 bg-background/40 p-2">
                <div className="overline text-[9px]">Activo</div>
                <div className="mt-1 truncate">{f.exposure?.asset || "—"}</div>
              </div>
              {f.exposure?.details?.port && (
                <>
                  <div className="border border-border/60 bg-background/40 p-2">
                    <div className="overline text-[9px]">Puerto / protocolo</div>
                    <div className="mt-1">{f.exposure.details.port}/{f.exposure.details.protocol || "tcp"}</div>
                  </div>
                  {f.exposure.details.service_hint && (
                    <div className="border border-border/60 bg-background/40 p-2">
                      <div className="overline text-[9px]">Servicio esperado</div>
                      <div className="mt-1">{f.exposure.details.service_hint}</div>
                    </div>
                  )}
                </>
              )}
              <div className="border border-border/60 bg-background/40 p-2">
                <div className="overline text-[9px]">Confirmado</div>
                <div className="mt-1">{fmtDate(f.exposure?.confirmed_at || f.first_seen)}</div>
              </div>
            </div>
            {f.exposure?.evidence && Object.keys(f.exposure.evidence).length > 0 && (
              <details className="mt-3">
                <summary className="cursor-pointer text-[10px] font-mono uppercase tracking-widest text-muted-foreground hover:text-foreground">
                  Ver datos crudos
                </summary>
                <pre className="mt-2 font-mono text-[11px] bg-background border border-border p-3 overflow-x-auto">
{JSON.stringify(f.exposure.evidence, null, 2)}
                </pre>
              </details>
            )}
          </section>

          {/* Vulnerability — only present when a CVE/exploit/KEV is correlated */}
          {f.vulnerability && (
            <section className="border border-sev-critical/40 bg-sev-critical/5 p-5"
                     data-testid="vulnerability-section">
              <div className="flex items-center gap-2 mb-2">
                <ShieldAlert className="w-4 h-4 text-sev-critical" />
                <div className="overline text-sev-critical">Vulnerabilidad correlacionada</div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs font-mono">
                <div className="border border-border/60 bg-background/40 p-2">
                  <div className="overline text-[9px]">CVE</div>
                  <div className="mt-1 text-primary">{f.vulnerability.cve || "—"}</div>
                </div>
                <div className="border border-border/60 bg-background/40 p-2">
                  <div className="overline text-[9px]">CVSS</div>
                  <div className="mt-1">{f.vulnerability.cvss ?? "—"}</div>
                </div>
                <div className="border border-border/60 bg-background/40 p-2">
                  <div className="overline text-[9px]">EPSS</div>
                  <div className="mt-1">{f.vulnerability.epss != null ? `${(f.vulnerability.epss * 100).toFixed(1)}%` : "—"}</div>
                </div>
                <div className="border border-border/60 bg-background/40 p-2">
                  <div className="overline text-[9px]">Confianza</div>
                  <div className="mt-1">{f.vulnerability.confidence?.replaceAll("_", " ") || "—"}</div>
                </div>
              </div>
              <div className="flex flex-wrap gap-2 mt-3 text-[10px] font-mono uppercase tracking-widest">
                {f.vulnerability.kev && <span className="border border-sev-critical/60 text-sev-critical px-1.5 py-0.5">KEV</span>}
                {f.vulnerability.exploit_available && <span className="border border-sev-high/60 text-sev-high px-1.5 py-0.5">exploit público</span>}
              </div>
              {f.vulnerability.note && (
                <p className="text-sm mt-3 leading-relaxed">{f.vulnerability.note}</p>
              )}
            </section>
          )}

          <section className="border border-border bg-card/60 p-5">
            <div className="overline mb-2">Impacto</div>
            <p className="text-sm whitespace-pre-wrap leading-relaxed">{f.business_impact || "—"}</p>
          </section>

          <section className="border border-border bg-card/60 p-5">
            <div className="overline mb-2">Remediación</div>
            <p className="text-sm leading-relaxed whitespace-pre-wrap">{f.remediation || "—"}</p>
            {f.references?.length > 0 && (
              <div className="mt-4">
                <div className="font-mono text-[10px] tracking-widest uppercase text-muted-foreground mb-2">Referencias</div>
                <div className="space-y-1">
                  {f.references.map((r) => (
                    <a key={r} href={r} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-xs text-primary hover:underline font-mono">
                      <ExternalLink className="w-3 h-3" />{r}
                    </a>
                  ))}
                </div>
              </div>
            )}
          </section>

          {f.kev_context?.in_catalog && (
            <section className="border border-sev-critical/40 bg-sev-critical/5 p-5 space-y-3"
                     data-testid="kev-context-section">
              <div className="flex items-center gap-2 mb-1">
                <ShieldAlert className="w-4 h-4 text-sev-critical" />
                <div>
                  <div className="overline text-sev-critical">CISA KEV · Contexto</div>
                  <div className="font-display text-base">{f.kev_context.name || f.cve}</div>
                </div>
              </div>
              {f.kev_context.description && (
                <p className="text-sm leading-relaxed">{f.kev_context.description}</p>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs font-mono">
                <KevRow icon={Building2} label="Vendor / Producto"
                        value={[f.kev_context.vendor, f.kev_context.product].filter(Boolean).join(" · ") || "—"} />
                <KevRow icon={Calendar} label="Añadido al catálogo"
                        value={f.kev_context.date_added
                          ? new Date(f.kev_context.date_added).toLocaleDateString()
                          : "—"} />
                <KevRow icon={Calendar} label="Fecha límite CISA (due date)"
                        value={f.kev_context.due_date
                          ? new Date(f.kev_context.due_date).toLocaleDateString()
                          : "—"}
                        tone={f.kev_context.due_date && new Date(f.kev_context.due_date) < new Date()
                          ? "critical" : undefined}
                        testid="kev-due-date" />
                <KevRow icon={Skull} label="Ransomware conocido"
                        value={f.kev_context.known_ransomware_bool ? "Sí" : "No"}
                        tone={f.kev_context.known_ransomware_bool ? "critical" : "low"}
                        testid="kev-ransomware" />
              </div>
              {f.kev_context.required_action && (
                <div className="border border-sev-critical/30 bg-background p-3"
                     data-testid="kev-required-action">
                  <div className="flex items-center gap-2 mb-1">
                    <Wrench className="w-3 h-3 text-sev-critical" />
                    <div className="overline text-sev-critical">Acción requerida por CISA</div>
                  </div>
                  <p className="text-sm leading-relaxed">{f.kev_context.required_action}</p>
                </div>
              )}
              <div className="text-[10px] font-mono text-muted-foreground">
                Fuente: <a href="https://www.cisa.gov/known-exploited-vulnerabilities-catalog"
                          target="_blank" rel="noopener noreferrer"
                          className="text-primary hover:underline">CISA Known Exploited Vulnerabilities Catalog</a>
              </div>
            </section>
          )}
        </div>

        <div className="space-y-4">
          <section className="border border-border bg-card/60 p-5">
            <div className="flex items-center gap-2 mb-3">
              <ShieldAlert className="w-4 h-4 text-primary" />
              <div className="overline">¿Por qué este puntaje?</div>
            </div>
            <div className="space-y-2">
              {f.risk_factors?.map((rf) => (
                <div key={rf.factor} className="flex items-center justify-between text-xs font-mono border-b border-border/50 pb-1.5">
                  <span className="text-muted-foreground">{rf.factor}</span>
                  <span className={cn(
                    "font-medium",
                    rf.contribution > 0 ? "text-sev-critical" : "text-sev-low"
                  )}>{rf.contribution > 0 ? "+" : ""}{rf.contribution}</span>
                </div>
              ))}
            </div>
          </section>

          <section className="border border-border bg-card/60 p-5 space-y-3">
            <div className="overline mb-2">Atributos</div>
            <Row label="Confianza" value={f.confidence != null ? `${f.confidence}%` : "—"} />
            <Row label="Activo" value={f.asset_label || "—"} />
            <Row label="SLA" value={f.sla_days != null ? `${f.sla_days} días` : "—"} />
            <Row label="Primera vez visto" value={fmtDate(f.first_seen)} />
            <Row label="Categoría" value={f.category || "—"} />
          </section>

          <section className="border border-border bg-card/60 p-5">
            <div className="overline mb-3">Seguimiento de remediación</div>
            <label className="block mb-3">
              <div className="font-mono text-[10px] tracking-widest uppercase text-muted-foreground mb-1">Estado</div>
              <select value={status} onChange={(e) => setStatus(e.target.value)}
                      className="w-full bg-background border border-border px-2 py-1.5 text-sm font-mono focus:border-primary outline-none"
                      data-testid="status-select">
                {["new","open","triaged","in_progress","mitigated","fixed","accepted","false_positive","reopened"].map(s => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </label>
            <label className="block mb-3">
              <div className="font-mono text-[10px] tracking-widest uppercase text-muted-foreground mb-1">Responsable</div>
              <input value={owner} onChange={(e) => setOwner(e.target.value)}
                     className="w-full bg-background border border-border px-2 py-1.5 text-sm font-mono focus:border-primary outline-none"
                     placeholder="equipo o correo" data-testid="owner-input" />
            </label>
            <button onClick={save} className="w-full bg-primary text-primary-foreground py-2 flex items-center justify-center gap-2 hover:bg-primary/90" data-testid="save-finding">
              <Save className="w-3 h-3" /> Guardar
            </button>
          </section>
        </div>
      </div>

      <FindingAIChat findingId={id} finding={f} />
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between text-xs border-b border-border/50 pb-1.5">
      <span className="font-mono text-[10px] tracking-widest uppercase text-muted-foreground">{label}</span>
      <span className="font-mono">{value}</span>
    </div>
  );
}

function KevRow({ icon: Icon, label, value, tone, testid }) {
  const cls = tone === "critical" ? "text-sev-critical"
            : tone === "low"      ? "text-sev-low"
            : "text-foreground";
  return (
    <div className="border border-border/60 bg-background/40 p-2" data-testid={testid}>
      <div className="flex items-center gap-1 overline text-[9px]">
        <Icon className="w-3 h-3" /> {label}
      </div>
      <div className={cn("mt-1", cls)}>{value}</div>
    </div>
  );
}
